import { lazy, Suspense } from "react";
import Header from "@/components/Header";
const FloatingTestCTA = lazy(() => import("@/components/FloatingTestCTA"));
import { useIsMobile } from "@/hooks/use-mobile";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ArrowRight, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import TestRequestFormWithCountries from "@/components/TestRequestFormWithCountries";
import StripeButtonWithCountries from "@/components/StripeButtonWithCountries";

import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";

const NosOffres = () => {
  // Track affiliate clicks on this page
  useAffiliateTracking();
  const isMobile = useIsMobile();
  const offers = [
    {
      name: "TEST",
      duration: "24H - ESSAI",
      price: "GRATUIT",
      pricePerMonth: null,
      popular: false,
      isTest: true,
      cta: "ESSAYEZ GRATUITEMENT",
      ctaLink: "",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
    {
      name: "3 MOIS",
      duration: "90 JOURS - ABONNEMENT",
      price: "25",
      pricePerMonth: "8,33",
      popular: false,
      isTest: false,
      stripeProductType: "3months" as const,
      cta: "PAYEZ MAINTENANT",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
    {
      name: "6 MOIS",
      duration: "180 JOURS - ABONNEMENT",
      price: "40",
      pricePerMonth: "6,67",
      popular: false,
      isTest: false,
      stripeProductType: "6months" as const,
      cta: "PAYEZ MAINTENANT",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
    {
      name: "12 MOIS",
      duration: "365 JOURS - ABONNEMENT",
      price: "60",
      pricePerMonth: "5",
      popular: true,
      isTest: false,
      stripeProductType: "12months" as const,
      cta: "PAYEZ MAINTENANT",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT VIP 24/7", included: true },
      ],
    },
    {
      name: "MENSUEL",
      duration: "ACCÈS MENSUEL",
      price: "9,99",
      pricePerMonth: "9,99",
      popular: false,
      isTest: false,
      isMonthly: true,
      stripeProductType: "monthly" as const,
      cta: "OBTENIR MON ACCÈS",
      features: [
        { text: "+15 000 CHAÎNES TV", included: true },
        { text: "+100 000 FILMS", included: true },
        { text: "+25 000 SÉRIES", included: true },
        { text: "FONCTIONNE DANS LE MONDE ENTIER", included: true },
        { text: "MISE À JOUR AUTO DU CONTENU", included: true },
        { text: "SUPPORT 7J/7", included: true },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Nos Offres IPTV - Abonnements | RoiDuStreaming</title>
        <meta name="description" content="Découvrez nos offres d'abonnement IPTV : Test gratuit 24h, 3 mois à 25€, 6 mois à 40€, 12 mois à 60€. +15 000 chaînes en HD/4K." />
        <meta name="keywords" content="abonnement IPTV, offres IPTV, prix IPTV, IPTV pas cher, IPTV France" />
        <link rel="canonical" href="https://roidustreaming.fr/nos-offres" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs />
        
        {/* Hero Section */}
        <section className="relative min-h-[40vh] flex items-center justify-center px-4 py-20">
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background"></div>
          <div className="relative z-10 max-w-5xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 gradient-text tracking-wide">
              NOS OFFRES D'ABONNEMENT
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Choisissez l'abonnement qui vous convient
            </p>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto">
            {/* Pricing Cards */}
            {(() => {
              const mainOffers = offers.filter(o => !o.isMonthly);
              const monthlyOffer = offers.find(o => o.isMonthly);
              
              // Mobile order: 12m, 6m, 3m, mensuel, gratuit
              const mobileOrder = isMobile 
                ? [...mainOffers].reverse().reduce((acc, offer, i) => {
                    // Insert monthly after 3 MOIS (index 2 in reversed = after 12m, 6m, 3m)
                    acc.push(offer);
                    if (i === 2 && monthlyOffer) acc.push(monthlyOffer);
                    return acc;
                  }, [] as typeof offers)
                : mainOffers;

              const renderCard = (offer: typeof offers[0], index: number) => (
                <Card 
                  key={`${offer.name}-${index}`} 
                  className={`relative overflow-hidden transition-all duration-300 hover:scale-105 rounded-xl border-2 flex flex-col ${
                    offer.popular 
                      ? 'bg-gradient-to-b from-primary to-primary/80 border-primary glow-primary' 
                      : 'bg-card border-primary/40 hover:border-primary/80'
                  }`}
                >
                  <CardContent className="p-6 text-center flex flex-col flex-1">
                    <h3 className={`text-2xl font-bold mb-2 tracking-wider ${
                      offer.popular ? 'text-primary-foreground' : 'text-primary'
                    }`}>
                      {offer.name}
                    </h3>
                    <p className={`text-sm mb-4 tracking-wide ${
                      offer.popular ? 'text-primary-foreground/80' : 'text-muted-foreground'
                    }`}>
                      {offer.duration}
                    </p>
                    
                    <div className="mb-4 min-h-[80px]">
                      <div className={`text-5xl font-black ${
                        offer.popular ? 'text-primary-foreground' : 'text-foreground'
                      }`}>
                        {offer.isTest ? offer.price : `${offer.price} €`}
                      </div>
                      {offer.pricePerMonth ? (
                        <p className={`text-sm mt-1 ${
                          offer.popular ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}>
                          soit {offer.pricePerMonth} €/mois
                        </p>
                      ) : (
                        <p className="text-sm mt-1 invisible">-</p>
                      )}
                    </div>
                    
                    {offer.isTest ? (
                      <div className="w-full mb-6">
                        <TestRequestFormWithCountries
                          trigger={
                            <Button 
                              className="w-full font-bold py-5 text-sm rounded-full transition-all duration-300 group btn-primary text-primary-foreground"
                            >
                              {offer.cta}
                              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </Button>
                          }
                        />
                      </div>
                    ) : (
                      <div className="w-full mb-6">
                        <StripeButtonWithCountries 
                          productType={offer.stripeProductType!}
                          className={`w-full font-bold py-5 text-sm rounded-full transition-all duration-300 group ${
                            offer.popular 
                              ? 'bg-background text-foreground hover:bg-background/90' 
                              : 'btn-primary text-primary-foreground'
                          }`}
                        >
                          {offer.cta}
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </StripeButtonWithCountries>
                      </div>
                    )}
                    
                    <ul className="space-y-3 text-left mb-4 flex-1">
                      {offer.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-secondary flex-shrink-0">✅</span>
                          <span className={`text-sm ${
                            offer.popular ? 'text-primary-foreground/90' : 'text-foreground/80'
                          }`}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className={`pt-4 border-t text-left text-xs ${
                      offer.popular ? 'border-primary-foreground/20' : 'border-border'
                    }`}>
                      <p className={offer.popular ? 'text-primary-foreground/80' : 'text-muted-foreground'}>
                        📚 Tutoriels fournis par email + disponibles sur{' '}
                        <Link 
                          to="/tutoriels" 
                          className={`underline hover:no-underline ${
                            offer.popular ? 'text-black font-semibold' : 'text-primary'
                          }`}
                        >
                          la page Tutoriels
                        </Link>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );

              return isMobile ? (
                /* Mobile: single column with custom order */
                <div className="grid grid-cols-1 gap-4">
                  {mobileOrder.map((offer, index) => renderCard(offer, index))}
                </div>
              ) : (
                /* Desktop: 4 cards top row + monthly full-width banner below */
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    {mainOffers.map((offer, index) => renderCard(offer, index))}
                  </div>
                  {monthlyOffer && (
                    <Card 
                      className="relative overflow-hidden transition-all duration-300 hover:scale-[1.02] rounded-xl border-2 bg-card border-primary/40 hover:border-primary/80"
                    >
                      <CardContent className="p-6 lg:p-8">
                        <div className="flex items-center gap-6 lg:gap-8">
                          <div className="text-center shrink-0">
                            <h3 className="text-xl lg:text-2xl font-bold tracking-wider text-primary">
                              {monthlyOffer.name}
                            </h3>
                            <p className="text-xs lg:text-sm tracking-wide text-muted-foreground mt-1">
                              {monthlyOffer.duration}
                            </p>
                          </div>
                          <div className="text-center shrink-0">
                            <div className="text-3xl lg:text-5xl font-black text-foreground">
                              {monthlyOffer.price} €
                            </div>
                            <p className="text-xs lg:text-sm mt-1 text-muted-foreground">
                              soit {monthlyOffer.pricePerMonth} €/mois
                            </p>
                          </div>
                          <ul className="flex-1 grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1.5">
                            {monthlyOffer.features.map((feature, i) => (
                              <li key={i} className="flex items-center gap-1.5">
                                <span className="text-secondary flex-shrink-0 text-sm">✅</span>
                                <span className="text-xs lg:text-sm text-foreground/80">{feature.text}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="shrink-0 w-[180px] lg:w-[220px]">
                            <StripeButtonWithCountries 
                              productType={monthlyOffer.stripeProductType!}
                              className="w-full font-bold py-4 lg:py-5 text-xs lg:text-sm rounded-full transition-all duration-300 group btn-primary text-primary-foreground"
                            >
                              {monthlyOffer.cta}
                              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </StripeButtonWithCountries>
                          </div>
                        </div>
                        <p className="text-center text-sm text-primary font-semibold mt-4">
                          💰 Économisez plus de 800 € par an par rapport aux plateformes classiques !
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              );
            })()}
            
            {/* Sub info */}
            <div className="flex justify-center gap-6 text-sm text-muted-foreground pt-10">
              <span className="flex items-center gap-1">
                ⚡ Installation 5 min
              </span>
              <span className="flex items-center gap-1">
                🛟 SAV 24/7
              </span>
            </div>
            
            {/* Contact */}
          </div>
        </section>

        <Footer />
        
        <Suspense fallback={null}>
          <FloatingTestCTA />
        </Suspense>
      </div>
    </>
  );
};

export default NosOffres;